// ExecutiveSimulationStudio.tsx - Executive Risk-Revenue Optimization Studio (Monte Carlo)
import React, { useState, useEffect, useRef } from 'react';
import { Calculator, TrendingUp, AlertTriangle, Target, FileText, Briefcase, Shield, DollarSign, BarChart3, Activity } from 'lucide-react';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import { executiveRiskEngine, type ExecutiveSimulationResults } from '../../../services/ExecutiveRiskRevenueEngine';
import { whatIfExpectedLoss, type ExpectedLossView } from '../../../services/expectedLoss';
import { formatWholeDollars, industryDiffers, revenueDiffers, whatIfLine, type WhatIfContext } from './whatIf';
import { authHeaders } from '../../../services/authHeaders';
import BoardReportGenerator from './BoardReportGenerator';
import ThreatScenarioCharts from './ThreatScenarioCharts';
import apiConfig from '../../../config/apiConfig';

/** The business-intelligence route's expectedLoss block (only the fields the Studio reads). */
interface BiExpectedLoss extends ExpectedLossView {
  annualRevenue: number | null;
  basePct: number | null;
  sizeMultiplier: number | null;
  // The industry the formula used (null when not known): the Studio's recorded industry.
  lossIndustry?: string | null;
  // Set only when revenue is the one missing input: what a typed revenue needs to be computed.
  whatIfInputs?: { basePct: number; sizeMultiplier: number; lossIndustry: string } | null;
}

/** The selector's fixed sectors; the formula's recorded industry is added when it is not one of them. */
const INDUSTRY_OPTIONS = [
  'Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 'Energy',
  'Transportation and Logistics', 'Real Estate', 'Hospitality and Tourism', 'Education',
];

/** A typed field that is blank or whitespace is not measured — never Number('') = 0. */
const parseTypedAmount = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/** What a finished run was computed from; the results and the Board Report describe THIS state. */
interface RunContext {
  revenue: number | null; // null = not measured (never typed, or typed 0)
  budget: number;
  industry: string;
  whatIf: WhatIfContext | null; // null = the recorded state
}

const finiteOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A recorded security budget: a positive finite number. Missing or 0 is unknown, not "$0". */
const knownBudget = (v: unknown): number | null => {
  const n = finiteOrNull(v);
  return n !== null && n > 0 ? n : null;
};

const BUDGET_REQUIRED_MESSAGE = 'Enter your current security budget to compare investment scenarios.';

interface Props {
  assessmentId?: string;
  companyData?: {
    name: string;
    // Null = not known (DefenseModule never invents one). The Studio's RECORDED industry is the
    // formula's (expectedLoss.lossIndustry), not this copy; this only seeds the field.
    industry: string | null;
    annualRevenue: number | null;
    currentCyberBudget: number | null;
  };
}

const ExecutiveSimulationStudioSimple: React.FC<Props> = ({ assessmentId, companyData }) => {
  console.log('🎯 ExecutiveSimulationStudioSimple mounting with updated navigation styling:', { assessmentId, companyData });
  
  const revenueMeasured = companyData?.annualRevenue !== null && companyData?.annualRevenue !== undefined;
  const [inputs, setInputs] = useState({
    // Seeded at 0 only as an editable-field default, never asserted as a measurement —
    // see annualRevenueUnmeasured, which gates the read-only summary display below.
    annualRevenue: revenueMeasured ? (companyData!.annualRevenue as number) : 0,
    // Same pattern: 0 is only the field's default while currentCyberBudgetUnmeasured is set; it is
    // never shown as "$0" and never reaches the engine (Run stays disabled until a budget is known).
    currentCyberBudget: knownBudget(companyData?.currentCyberBudget) ?? 0,
    // Replaced by the formula's industry when the BI block arrives; never a 'General' default.
    industry: companyData?.industry ?? ''
  });
  const [annualRevenueUnmeasured, setAnnualRevenueUnmeasured] = useState(!revenueMeasured);
  const [currentCyberBudgetUnmeasured, setCurrentCyberBudgetUnmeasured] = useState(
    knownBudget(companyData?.currentCyberBudget) === null,
  );
  // The BI expectedLoss block: null until the response arrives, and null when it failed or had
  // none (not measured). biResolved gates the Run button, so a run never races the fetch.
  const [biExpectedLoss, setBiExpectedLoss] = useState<BiExpectedLoss | null>(null);
  const [biResolved, setBiResolved] = useState(!assessmentId);
  // Set once the executive edits revenue, so the arriving BI revenue never overwrites their typing.
  const revenueEditedRef = useRef(false);
  const industryEditedRef = useRef(false);
  // The recorded industry is the one the formula used, carried in the expectedLoss block — not
  // companyData's copy (read from a superseded mirror) and never a default. Null = not known.
  const recordedIndustry: string | null = biExpectedLoss?.lossIndustry ?? null;
  const [simulationResults, setSimulationResults] = useState<ExecutiveSimulationResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [outsidePressures, setOutsidePressures] = useState<any>(null);
  const [prismData, setPrismData] = useState<any>(null);
  const [crsScore, setCrsScore] = useState<number | null>(null);
  const [showBoardReport, setShowBoardReport] = useState(false);
  const [runContext, setRunContext] = useState<RunContext | null>(null);
  const runWhatIfText = whatIfLine(runContext?.whatIf);
  // No recommendation: no ROI or payback is shown (a return on a spend the model does not recommend).
  const hasRecommendation = simulationResults?.recommendedScenario != null;
  // Run waits for the BI expected loss and for a known budget (never an invented $0 to the engine).
  const runBlocked = isRunning || !biResolved || currentCyberBudgetUnmeasured;

  // Fetch actual cyber budget from assessment to align with Investment Optimization
  useEffect(() => {
    console.log('🔍 useEffect triggered for budget fetch. AssessmentId:', assessmentId);
    // Forget the previous assessment's ALE first: a failed or absent fetch must read as not
    // measured, never as the last assessment's number.
    setBiExpectedLoss(null);
    setBiResolved(!assessmentId);
    // A new assessment: its BI revenue seeds the input again, and the last assessment's results
    // (and what-if state) are not shown as this one's.
    revenueEditedRef.current = false;
    industryEditedRef.current = false;
    setSimulationResults(null);
    setRunContext(null);
    setShowBoardReport(false);
    let cancelled = false;

    const fetchActualBudget = async () => {
      if (assessmentId) {
        try {
          console.log('💰 Fetching actual cyber budget from assessment for alignment...');
          // Gated route (v3.65) — without credentials this 401s and the budget silently
          // stays un-aligned. See services/authHeaders.ts.
          const response = await fetch(
            `${apiConfig.getApiBaseUrl()}/business-intelligence/${assessmentId}`,
            { headers: authHeaders() }
          );
          if (!response.ok) {
            console.warn(
              `⚠️ Budget alignment fetch failed (${response.status}) — keeping the un-aligned budget rather than silently proceeding as if none existed`
            );
          }
          if (response.ok) {
            const data = await response.json();
            if (cancelled) return;
            // The formula ALE the simulation is anchored to (Task 8). Absent = not measured.
            const block: BiExpectedLoss | null = data.expectedLoss ?? null;
            setBiExpectedLoss(block);
            // The recorded revenue is the one the formula used, not companyData's copy: show it,
            // so the recorded state reads as recorded (no What-if) until the executive edits it.
            const recordedRevenue = finiteOrNull(block?.annualRevenue);
            if (recordedRevenue !== null && !revenueEditedRef.current) {
              setInputs(prev => ({ ...prev, annualRevenue: recordedRevenue }));
              setAnnualRevenueUnmeasured(false);
            }
            // Same for the industry: the formula's, shown as recorded until the executive edits it.
            if (block && !industryEditedRef.current) {
              setInputs(prev => ({ ...prev, industry: block.lossIndustry ?? '' }));
            }
            console.log('📊 Business Intelligence API response:', {
              hasInvestmentScenarios: !!data.investmentScenarios,
              scenarioCount: data.investmentScenarios?.length,
              scenarios: data.investmentScenarios?.map(s => ({ label: s.label, investment: s.investment }))
            });

            // Backend doesn't return 'success' field, check for investmentScenarios directly
            if (data.investmentScenarios && Array.isArray(data.investmentScenarios)) {
              const currentScenario = data.investmentScenarios.find(s => s.isCurrent || s.label === 'Current Budget');
              const alignedBudget = knownBudget(currentScenario?.investment);
              if (alignedBudget !== null) {
                console.log(`✅ Aligned budget with Investment Optimization: $${(alignedBudget/1000000).toFixed(2)}M`);
                setInputs(prev => ({
                  ...prev,
                  currentCyberBudget: alignedBudget
                }));
                setCurrentCyberBudgetUnmeasured(false);
              } else {
                console.warn('⚠️ No current budget scenario found in:', data.investmentScenarios);
              }
            } else {
              console.warn('⚠️ No investmentScenarios found in API response');
            }
          } else {
            console.error(`❌ API returned status ${response.status}`);
          }
        } catch (error) {
          console.error('❌ Error fetching actual budget for alignment:', error);
        } finally {
          // Success or failure, the expected loss is now known (a failure is genuinely not measured).
          if (!cancelled) setBiResolved(true);
        }
      }
    };

    fetchActualBudget();
    return () => { cancelled = true; };
  }, [assessmentId]);

  /**
   * The expected loss a run would use, and whether it is a what-if. The BI block is used as-is
   * for the recorded revenue and industry; a typed revenue or a changed industry recomputes it by
   * the same formula (whatIfExpectedLoss). With no BI block there is nothing to recompute from.
   */
  const currentAnchor = (): { anchor: ExpectedLossView | null; whatIf: WhatIfContext | null } => {
    if (!biExpectedLoss) return { anchor: null, whatIf: null };
    const ctx: WhatIfContext = {
      revenue: inputs.annualRevenue,
      recordedRevenue: finiteOrNull(biExpectedLoss.annualRevenue),
      industry: inputs.industry,
      recordedIndustry,
    };
    // An unmeasured revenue field is a change only when it cleared a recorded revenue.
    const revenueChanged = annualRevenueUnmeasured ? ctx.recordedRevenue !== null : revenueDiffers(ctx);
    if (!revenueChanged && !industryDiffers(ctx)) return { anchor: biExpectedLoss, whatIf: null };
    // Recorded block measured: its own inputs. Not measured because revenue alone is missing: the
    // whatIfInputs it carries. Anything else missing: nothing to compute from (stays not measured).
    const source = biExpectedLoss.measured ? biExpectedLoss : biExpectedLoss.whatIfInputs ?? null;
    return {
      anchor: whatIfExpectedLoss({
        annualRevenue: annualRevenueUnmeasured ? null : inputs.annualRevenue,
        basePct: source?.basePct,
        sizeMultiplier: source?.sizeMultiplier,
        industry: inputs.industry,
      }),
      whatIf: { ...ctx, revenueChanged },
    };
  };


  // Fetch outside pressures and PRISM data when component mounts
  useEffect(() => {
    if (assessmentId) {
      console.log('🌍 Fetching outside pressures for assessment:', assessmentId);
      fetch(`${apiConfig.getApiBaseUrl()}/risk-intelligence/outside-pressures/${assessmentId}`, { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            console.log('✅ Outside pressures loaded:', data.outsidePressures);
            setOutsidePressures(data.outsidePressures);
          } else {
            console.warn('⚠️ Failed to load outside pressures:', data.error);
          }
        })
        .catch(error => {
          console.error('❌ Error fetching outside pressures:', error);
        });

      // Fetch CRS score for board report badge
      fetch(`${apiConfig.getApiBaseUrl()}/crs/${assessmentId}`, { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data?.score != null) {
            setCrsScore(data.data.score);
          }
        })
        .catch(() => { /* CRS optional — badge falls back to financial derivation */ });

      // Fetch PRISM data for board report (Phase 5)
      fetch(`${apiConfig.getApiBaseUrl()}/foundations/prism-data/${assessmentId}`, {
        headers: authHeaders(),
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data?.connected !== false) {
            console.log('✅ PRISM data loaded for board report');
            setPrismData(data.data);
          }
        })
        .catch(() => {
          // PRISM unavailable — board report works without it
        });
    }
  }, [assessmentId]);

  // null = not measured (Task 8: the engine returns null rather than a stand-in number).
  const formatCurrency = (amount: number | null): string => {
    if (amount === null || !Number.isFinite(amount)) return 'Not measured';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const formatPercent = (decimal: number | null): string => {
    if (decimal === null || !Number.isFinite(decimal)) return 'Not measured';
    return `${(decimal * 100).toFixed(1)}%`;
  };

  // Fallback risk factors when assessment data is not available
  const getFallbackRiskFactors = () => {
    console.log('🔄 Using fallback risk factors (no assessment data available)');
    return [
      {
        id: 'governance_fallback',
        name: 'Security Governance Framework',
        category: 'governance' as const,
        currentScore: 65,
        weight: 1.5,
        frequency: 0.14,
        impact: 5,
        confidenceLevel: 0.85
      },
      {
        id: 'tech_debt_fallback',
        name: 'Legacy System Vulnerabilities',
        category: 'technical_debt' as const,
        currentScore: 72,
        weight: 1.8,
        frequency: 0.21,
        impact: 7,
        confidenceLevel: 0.90
      },
      {
        id: 'culture_fallback',
        name: 'Security Awareness Culture',
        category: 'culture' as const,
        currentScore: 58,
        weight: 1.3,
        frequency: 0.24,
        impact: 4,
        confidenceLevel: 0.75
      }
    ];
  };

  const runSimulation = async () => {
    setIsRunning(true);
    // Decided from the inputs at the moment of the click; the results describe this state.
    const { anchor, whatIf } = currentAnchor();
    const runInputs = {
      // Not measured (never entered, or typed 0) travels as null, so nothing prints "$0".
      revenue: !annualRevenueUnmeasured && inputs.annualRevenue > 0 ? inputs.annualRevenue : null,
      budget: inputs.currentCyberBudget,
      industry: inputs.industry,
    };

    try {
      console.log('🎲 Starting Executive Monte Carlo simulation...');
      
      let riskFactors = [];
      
      // Fetch dynamic risk factors from assessment responses
      if (assessmentId) {
        try {
          console.log(`📊 Fetching dynamic risk factors for assessment ${assessmentId}`);
          const riskResponse = await fetch(`${apiConfig.getApiBaseUrl()}/assessments/risk-factors/${assessmentId}`, { headers: authHeaders() });
          
          if (riskResponse.ok) {
            const riskData = await riskResponse.json();
            
            if (riskData.success && riskData.riskFactors?.length > 0) {
              console.log(`✅ Loaded ${riskData.riskFactors.length} dynamic risk factors from assessment`);
              riskFactors = riskData.riskFactors.map((factor: any) => ({
                id: factor.id,
                name: factor.name,
                category: factor.category as 'governance' | 'technical_debt' | 'culture' | 'third_party' | 'budget' | 'people',
                currentScore: factor.currentScore,
                weight: factor.weight,
                frequency: factor.frequency,
                impact: factor.impact,
                confidenceLevel: factor.confidenceLevel
              }));
            } else {
              console.warn('⚠️ No assessment risk factors found, using fallback model');
              riskFactors = getFallbackRiskFactors();
            }
          } else {
            console.warn('⚠️ Risk factors API failed, using fallback model');
            riskFactors = getFallbackRiskFactors();
          }
        } catch (error) {
          console.error('❌ Error fetching dynamic risk factors:', error);
          riskFactors = getFallbackRiskFactors();
        }
      } else {
        console.log('⚠️ No assessment ID provided, using fallback risk factors');
        riskFactors = getFallbackRiskFactors();
      }

      // Create risk profile with dynamic or fallback data
      const riskProfile = {
        annualRevenue: inputs.annualRevenue,
        industry: inputs.industry,
        companySize: inputs.annualRevenue > 100000000 ? 'Large' : 'Medium',
        riskFactors
      };

      // Max must accommodate current budget + growth room even if already above industry %
      const budgetConstraints = {
        min: inputs.currentCyberBudget * 1.1,
        max: Math.max(inputs.annualRevenue * 0.08, inputs.currentCyberBudget * 2.5)
      };

      console.log(`💰 CRITICAL: Passing currentCyberBudget to engine: $${(inputs.currentCyberBudget / 1000000).toFixed(2)}M`);
      console.log(`📊 Full inputs state:`, inputs);

      const results = await executiveRiskEngine.runExecutiveSimulation(
        riskProfile,
        inputs.currentCyberBudget,
        budgetConstraints,
        assessmentId, // NEW: Pass assessmentId for outside pressures integration
        anchor
      );

      console.log('✅ Monte Carlo simulation completed:', results);
      setSimulationResults(results);
      setRunContext({ ...runInputs, whatIf });
      
    } catch (error) {
      console.error('❌ Executive simulation failed:', error);
      alert('Simulation failed. Please check the console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  // Card hover effect style
  const cardStyle = {
    background: 'var(--ra-glass-bg)',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid var(--ra-glass-border)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default'
  };

  const hoverableCardStyle = {
    ...cardStyle,
    cursor: 'pointer'
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, var(--ra-deep-blue), rgba(0, 102, 255, 0.08))',
      padding: '20px'
    }}>
      
      {/* Header Section with Gradient */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ 
            padding: '14px',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
            borderRadius: '16px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}>
            <Calculator size={32} color="white" />
          </div>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0 0 5px 0',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>Financial Impact Analysis <HelpTooltip contentKey="defense.simulation" /></span>
            </h1>
            <p style={{ fontSize: '18px', color: 'var(--ra-text-secondary)', margin: 0 }}>
              Monte Carlo Loss Modeling for Strategic Risk Decisions
            </p>
          </div>
        </div>
        
        {companyData && (
          <div style={{ 
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)',
            marginBottom: '20px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Briefcase aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ fontWeight: '600', color: 'var(--ra-text-primary)', fontSize: '16px' }}>{companyData.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarSign aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ color: '#a8b2d1' }}>Revenue: <strong>{annualRevenueUnmeasured || inputs.annualRevenue <= 0 ? 'Not measured — enter below' : formatCurrency(inputs.annualRevenue)}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ color: '#a8b2d1' }}>Security Budget: <strong>{currentCyberBudgetUnmeasured ? 'Not measured — enter below' : formatCurrency(inputs.currentCyberBudget)}</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Controls Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '40px',
        maxWidth: '1200px',
        margin: '0 auto 40px'
      }}>
        
        {/* Annual Revenue Input */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Annual Revenue ($)
          </label>
          <input
            type="number"
            value={annualRevenueUnmeasured ? '' : inputs.annualRevenue}
            onChange={(e) => {
              revenueEditedRef.current = true;
              // A blank entry is NOT measured (never Number('') = 0); a typed 0 the formula reads as not measured.
              const typed = parseTypedAmount(e.target.value);
              setInputs(prev => ({ ...prev, annualRevenue: typed ?? 0 }));
              setAnnualRevenueUnmeasured(typed === null);
            }}
            aria-label="Annual revenue"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
            placeholder="10000000"
          />
        </div>
        
        {/* Cyber Budget Input */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Current Cyber Budget ($)
          </label>
          <input
            type="number"
            value={currentCyberBudgetUnmeasured ? '' : inputs.currentCyberBudget}
            onChange={(e) => {
              // A blank entry is NOT measured: Run stays disabled with the reason (never a $0 budget).
              const typed = parseTypedAmount(e.target.value);
              setInputs(prev => ({ ...prev, currentCyberBudget: typed ?? 0 }));
              setCurrentCyberBudgetUnmeasured(typed === null);
            }}
            aria-label="Current cyber budget"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
            placeholder="350000"
          />
        </div>
        
        {/* Industry Select */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Industry Sector
          </label>
          <select
            value={inputs.industry}
            onChange={(e) => {
              industryEditedRef.current = true;
              setInputs(prev => ({ ...prev, industry: e.target.value }));
            }}
            aria-label="Industry sector"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none',
              cursor: 'pointer'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
          >
            {inputs.industry === '' && <option value="">Not measured — select</option>}
            {/* The recorded (formula) industry and the current value are always selectable, even when
                they are not one of the fixed sectors, so the field never shows a different industry. */}
            {[...new Set([...INDUSTRY_OPTIONS, recordedIndustry ?? '', inputs.industry])]
              .filter(v => v !== '')
              .map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        
        {/* Reset to Assessment Values */}
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => {
              // Back to the recorded state: the revenue the formula used, else the company's copy.
              const recordedRevenue = finiteOrNull(biExpectedLoss?.annualRevenue);
              const companyRevenue = finiteOrNull(companyData?.annualRevenue);
              const revenue = recordedRevenue ?? companyRevenue;
              revenueEditedRef.current = false;
              industryEditedRef.current = false;
              setInputs({
                annualRevenue: revenue ?? 0,
                currentCyberBudget: knownBudget(companyData?.currentCyberBudget) ?? 0,
                industry: recordedIndustry ?? ''
              });
              setAnnualRevenueUnmeasured(revenue === null);
              setCurrentCyberBudgetUnmeasured(knownBudget(companyData?.currentCyberBudget) === null);
            }}
            style={{
              width: '100%',
              background: 'transparent',
              color: 'var(--ra-text-secondary)',
              fontWeight: '500',
              fontSize: '13px',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px dashed var(--ra-glass-border)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ra-cyber-aqua)'; e.currentTarget.style.color = 'var(--ra-cyber-aqua)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ra-glass-border)'; e.currentTarget.style.color = 'var(--ra-text-secondary)'; }}
          >
            Reset to Assessment Values
          </button>
        </div>

        {/* Run Simulation Button */}
        <div style={cardStyle}>
          <button
            onClick={runSimulation}
            // Wait for the assessment's expected loss; running earlier would read "Not measured".
            disabled={runBlocked}
            style={{
              width: '100%',
              background: runBlocked
                ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                : 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              padding: '15px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: runBlocked ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: runBlocked ? 0.7 : 1,
              transition: 'all 0.3s',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            onMouseOver={(e) => {
              if (!runBlocked) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            {isRunning ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Running Analysis...
              </>
            ) : (
              <>
                <Calculator size={20} />
                Run Financial Impact Analysis
              </>
            )}
          </button>
          {/* The reason in words, not just a greyed button. */}
          {currentCyberBudgetUnmeasured && (
            <p data-testid="run-blocked-reason" style={{ fontSize: '13px', color: 'var(--ra-text-primary)', margin: '10px 0 0 0' }}>
              {BUDGET_REQUIRED_MESSAGE}
            </p>
          )}
        </div>
      </div>

      {/* Results Display */}
      {simulationResults ? (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Current Risk Exposure Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(255, 68, 68, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-danger)',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AlertTriangle aria-hidden="true" size={20} color="white" />
              </div>
              Current Risk Exposure
            </h2>

            {runWhatIfText && (
              <p style={{
                margin: '0 0 16px 0',
                padding: '8px 12px',
                border: '2px dashed var(--ra-warning)',
                borderRadius: '8px',
                color: 'var(--ra-text-primary)',
                fontSize: '14px',
                fontWeight: '600'
              }}>{runWhatIfText}</p>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div data-testid="studio-expected-annual-loss" style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Expected annual loss
                </div>
                {/* A what-if is marked in words, not colour: the badge text says it. */}
                {runContext?.whatIf && (
                  <span style={{
                    display: 'inline-block',
                    marginBottom: '6px',
                    padding: '2px 10px',
                    border: '1px dashed var(--ra-warning)',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: 'var(--ra-text-primary)'
                  }}>What-if</span>
                )}
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {formatWholeDollars(simulationResults.currentState.expectedAnnualLoss)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--ra-text-secondary)', marginTop: '6px' }}>
                  Revenue at risk × annual probability of a loss event (IRIS 2022)
                </div>
              </div>

              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Value at Risk (95th Percentile)
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {formatCurrency(simulationResults.currentState.percentile95Loss)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--ra-text-secondary)', fontStyle: 'italic', marginTop: '6px' }}>
                  Modelled distribution, anchored to expected annual loss
                </div>
              </div>

              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Loss Magnitude (% Revenue)
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {/* Over the revenue the run used, not whatever is typed now. */}
                  {formatPercent(simulationResults.currentState.percentile95Loss !== null && runContext?.revenue != null
                    ? simulationResults.currentState.percentile95Loss / runContext.revenue
                    : null)}
                </div>
              </div>
            </div>

            {/* Loss Decomposition */}
            {simulationResults.currentState.lossDecomposition && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#ccd6f6', marginBottom: '12px' }}>
                  Loss Decomposition
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div style={{
                    background: 'rgba(255, 165, 0, 0.08)',
                    border: '1px solid rgba(255, 165, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '14px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#9a3412', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Primary Loss
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#c2410c', marginBottom: '4px' }}>
                      {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.total)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#78350f' }}>
                      Productivity: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.productivity)} |
                      Response: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.response)} |
                      Replacement: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.replacement)}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 68, 68, 0.08)',
                    border: '1px solid rgba(255, 68, 68, 0.2)',
                    borderRadius: '8px',
                    padding: '14px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Secondary Loss
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--ra-danger)', marginBottom: '4px' }}>
                      {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.total)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#7f1d1d' }}>
                      Regulatory: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.regulatory)} |
                      Reputation: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.reputation)} |
                      Legal: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.legal)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Threat Scenario Charts — Scatter Plot + Loss Exceedance Curve */}
          {simulationResults.scenarioResults && simulationResults.currentState.expectedAnnualLoss !== null && (
            <ThreatScenarioCharts
              scenarioResults={simulationResults.scenarioResults}
              optimizedScenarios={simulationResults.optimizedScenarios}
              recommendedScenario={simulationResults.recommendedScenario}
              formatCurrency={formatCurrency}
            />
          )}

          {/* Recommended Investment Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 255, 136, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-success)',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <TrendingUp aria-hidden="true" size={20} color="white" />
              </div>
              Recommended Investment Strategy
            </h2>

            {/* Task 8: null when not measured, or when no scenario pays for itself at the ALE —
                the board summary's first recommendation says which, in plain words. */}
            {simulationResults.recommendedScenario === null ? (
              <p style={{ fontSize: '16px', color: 'var(--ra-text-primary)', margin: 0 }}>
                {simulationResults.boardSummary.recommendations[0]}
              </p>
            ) : (
            <div style={{
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Additional Investment
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {formatCurrency(simulationResults.recommendedScenario.budgetIncrease)}
                </div>
              </div>
              
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Projected Annual ROI
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {formatPercent(simulationResults.recommendedScenario.projectedROI)}
                </div>
              </div>
              
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Payback Period
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {Number.isFinite(simulationResults.recommendedScenario.paybackPeriod) ? `${simulationResults.recommendedScenario.paybackPeriod.toFixed(1)} months` : 'Not measured'}
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Executive Summary Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-cyber-aqua)',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <BarChart3 aria-hidden="true" size={20} color="white" />
              </div>
              Executive Summary
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              <div>
                <h3 style={{ 
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ccd6f6',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Activity aria-hidden="true" size={18} color="var(--ra-warning)" />
                  Key Findings
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {simulationResults.boardSummary.keyFindings.slice(0, 3).map((finding, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#a8b2d1',
                      lineHeight: '1.5'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#fbbf24',
                        marginTop: '6px',
                        flexShrink: 0
                      }} />
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 style={{ 
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ccd6f6',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Target aria-hidden="true" size={18} color="var(--ra-success)" />
                  Recommendations
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {simulationResults.boardSummary.recommendations.slice(0, 3).map((rec, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#a8b2d1',
                      lineHeight: '1.5'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'var(--ra-success)',
                        marginTop: '6px',
                        flexShrink: 0
                      }} />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Budget Scenarios Comparison */}
          {simulationResults.optimizedScenarios.length > 0 && (
            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(124, 58, 237, 0.08) 100%)',
              borderLeft: '4px solid #7c3aed',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'var(--ra-text-primary)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <DollarSign aria-hidden="true" size={20} color="white" />
                </div>
                Investment Scenarios
              </h2>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--ra-glass-border)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Scenario</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Investment</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Risk Reduction</th>
                      {hasRecommendation && <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ROI</th>}
                      {hasRecommendation && <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payback</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {simulationResults.optimizedScenarios.map((scenario, idx) => {
                      const isRecommended = scenario.scenarioName === simulationResults.recommendedScenario?.scenarioName;
                      return (
                        <tr key={idx} style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          background: isRecommended ? 'rgba(0, 255, 136, 0.08)' : 'transparent'
                        }}>
                          <td style={{ padding: '12px', fontWeight: isRecommended ? '700' : '500', color: 'var(--ra-text-primary)' }}>
                            {scenario.scenarioName}
                            {isRecommended && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', background: 'rgba(0, 217, 255, 0.2)', color: '#00d9ff', padding: '2px 8px', borderRadius: '10px', border: '1px solid rgba(0, 217, 255, 0.4)', fontWeight: '600' }}>Recommended</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px', color: scenario.budgetIncrease >= 0 ? '#ccd6f6' : 'var(--ra-warning)', fontWeight: '500' }}>{scenario.budgetIncrease >= 0 ? '+' : '-'}{formatCurrency(Math.abs(scenario.budgetIncrease))}</td>
                          <td style={{ textAlign: 'right', padding: '12px', color: 'var(--ra-success)', fontWeight: '600' }}>{formatPercent(scenario.projectedRiskReduction)}</td>
                          {hasRecommendation && <td style={{ textAlign: 'right', padding: '12px', color: '#ccd6f6', fontWeight: '500' }}>{formatPercent(scenario.projectedROI)}</td>}
                          {hasRecommendation && <td style={{ textAlign: 'right', padding: '12px', color: '#ccd6f6' }}>{Number.isFinite(scenario.paybackPeriod) ? `${scenario.paybackPeriod.toFixed(1)} mo` : 'Not measured'}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Generate Board Report Button */}
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button
              onClick={() => setShowBoardReport(true)}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <FileText aria-hidden="true" size={20} />
              Generate Board Report
            </button>
          </div>

        </div>
      ) : (
        /* Ready State Card */
        <div style={{ 
          ...cardStyle,
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
          background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}>
            <Activity aria-hidden="true" size={40} color="white" />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--ra-text-primary)' }}>
            Ready for Financial Impact Analysis
          </h2>
          <p style={{ fontSize: '16px', color: 'var(--ra-text-secondary)', margin: 0, lineHeight: '1.6' }}>
            Click "Run Financial Impact Analysis" to calculate the expected annual loss and its distribution using
            10,000 Monte Carlo iterations with loss decomposition and budget optimization.
          </p>
        </div>
      )}

      {/* Board Report Modal */}
      {showBoardReport && simulationResults && (
        <BoardReportGenerator
          simulationResults={simulationResults}
          companyData={{
            name: companyData?.name || 'Sample Corporation',
            // The state the results were computed from, not whatever is typed now.
            industry: runContext?.industry ?? inputs.industry,
            annualRevenue: runContext ? runContext.revenue : null,
            currentCyberBudget: runContext?.budget ?? inputs.currentCyberBudget
          }}
          outsidePressures={outsidePressures}
          prismData={prismData}
          crsScore={crsScore}
          whatIf={runContext?.whatIf ?? null}
          onClose={() => setShowBoardReport(false)}
        />
      )}

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ExecutiveSimulationStudioSimple;